import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import axios, {CancelToken, CancelTokenSource} from 'axios';
import {Alert, Box, Button, CircularProgress, Stack, Typography} from '@mui/material';
import {Add, Refresh} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {ContentPage} from '../content-page/content-page';
import GenericHeader from '../generic-header/generic-header';
import {LoadingContent} from '../loading-content/loading-content';
import {ReminderCard} from '../reminder-card/reminder-card';
import {ReminderFormDialog} from '../reminder-form-dialog/reminder-form-dialog';
import DeleteResourceConfirmationModal from '../delete-resource-confirmation-modal';
import {Reminder} from '../../backend/interfaces/reminder';
import {PostReminder} from '../../backend/requests/post-reminder';
import {AuthContext} from '../../contexts/auth/context';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {
  ReminderFormValues,
  buildReminderPayload,
} from '../../screens/private/office/informal-caregiver/reminder/reminder-form.utils';
import {PutReminder} from '../../backend/requests/put-reminder';
import {translations} from '../../i18n/translations';

export function RemindersContent({seniorId, api, canWrite = true}: RemindersContentProps) {
  const {t} = useTranslation();
  const authContext = useContext(AuthContext);
  const snackBarContext = useSnackBar();
  const currentUserId = authContext.state.session?.currentUser.id || '';
  const canManageReminders = canWrite;

  const [reminders, setReminders] = useState<Array<IdentifiedReminder>>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextServerSkip, setNextServerSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [formTarget, setFormTarget] = useState<{reminder?: IdentifiedReminder}>();
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IdentifiedReminder>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingActiveIds, setPendingActiveIds] = useState<Array<string>>([]);
  // Prevent a pending pagination request from appending to a freshly reloaded list.
  const requestVersionRef = useRef(0);

  const showToast = useCallback(
    (severity: 'success' | 'error', message: string) => {
      void snackBarContext.setOfficeToastNotification({severity, message});
    },
    [snackBarContext]
  );

  const refreshFirstPage = () => {
    requestVersionRef.current += 1;
    setReloadKey((key) => key + 1);
  };

  useEffect(() => {
    let ignore = false;
    const source: CancelTokenSource = axios.CancelToken.source();
    setReminders([]);
    setTotalCount(0);
    setNextServerSkip(0);
    setIsLoading(true);
    setIsLoadingMore(false);
    setLoadError(false);
    setLoadMoreError(false);

    const fetchReminders = async () => {
      try {
        const result = await api.get(REMINDER_PAGE_SIZE, 0, seniorId, source.token);
        if (ignore) return;
        setReminders(sortReminders(remindersForSenior(result.data, seniorId)));
        setTotalCount(result.totalCount);
        setNextServerSkip(result.data.length);
      } catch (error) {
        if (ignore || axios.isCancel(error)) return;
        console.debug('Could not load informal caregiver reminders.', error);
        setLoadError(true);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    void fetchReminders();
    return () => {
      ignore = true;
      requestVersionRef.current += 1;
      source.cancel();
    };
  }, [reloadKey, api, seniorId]);

  const handleLoadMore = async () => {
    if (!seniorId || isLoading || isLoadingMore || nextServerSkip >= totalCount) return;
    const requestVersion = requestVersionRef.current;
    setIsLoadingMore(true);
    setLoadMoreError(false);

    try {
      const result = await api.get(REMINDER_PAGE_SIZE, nextServerSkip, seniorId);
      if (requestVersion !== requestVersionRef.current) return;
      const newReminders = remindersForSenior(result.data, seniorId);
      setReminders((current) => {
        const currentIds = new Set(current.map((reminder) => reminder.id));
        return sortReminders([
          ...current,
          ...newReminders.filter((reminder) => !currentIds.has(reminder.id)),
        ]);
      });
      setTotalCount(result.totalCount);
      setNextServerSkip((current) => current + result.data.length);
    } catch (error) {
      if (requestVersion !== requestVersionRef.current || axios.isCancel(error)) return;
      console.debug('Could not load more informal caregiver reminders.', error);
      setLoadMoreError(true);
    } finally {
      if (requestVersion === requestVersionRef.current) setIsLoadingMore(false);
    }
  };

  const handleCreateReminder = () => {
    if (canWrite && seniorId && currentUserId) setFormTarget({});
  };

  const handleEditReminder = (reminder: IdentifiedReminder) => {
    if (canWrite && seniorId && !pendingActiveIds.includes(reminder.id)) {
      setFormTarget({reminder});
    }
  };

  const handleOpenDelete = (reminder: IdentifiedReminder) => {
    if (canWrite && seniorId && !pendingActiveIds.includes(reminder.id)) {
      setDeleteTarget(reminder);
    }
  };

  const handleSaveReminder = async (values: ReminderFormValues) => {
    if (!canWrite || !formTarget || isSaving) return;
    const editing = formTarget.reminder;
    if (!editing && !currentUserId) return;
    const payload: PostReminder = buildReminderPayload(values, currentUserId, seniorId, editing);
    setIsSaving(true);

    try {
      const savedReminder = editing
        ? await api.updateById(editing.id, payload)
        : await api.create(payload);
      if (!savedReminder.id || savedReminder.toUserId !== seniorId) {
        showToast('error', t(translations.reminders.save_error));
        return;
      }
      setFormTarget(undefined);
      showToast('success', t(translations.reminders.save_success));
      refreshFirstPage();
    } catch (error) {
      console.debug('Could not save senior reminder.', error);
      showToast('error', t(translations.reminders.save_error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteReminder = async () => {
    if (!canWrite || !deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      await api.deleteById(deleteTarget.id);
      setDeleteTarget(undefined);
      showToast('success', t(translations.reminders.delete_success));
      refreshFirstPage();
    } catch (error) {
      console.debug('Could not delete senior reminder.', error);
      showToast('error', t(translations.reminders.delete_error));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleActive = async (reminder: IdentifiedReminder, isActive: boolean) => {
    if (!canWrite || !seniorId || pendingActiveIds.includes(reminder.id)) return;
    setPendingActiveIds((current) => [...current, reminder.id]);
    try {
      const updatedReminder = await api.updateActiveStatusById(reminder.id, isActive);
      if (
        !updatedReminder.id ||
        updatedReminder.id !== reminder.id ||
        updatedReminder.toUserId !== seniorId
      ) {
        showToast('error', t(translations.reminders.active_error));
        return;
      }
      const identifiedUpdatedReminder = updatedReminder as IdentifiedReminder;
      setReminders((current) =>
        sortReminders(
          current.map((item) =>
            item.id === identifiedUpdatedReminder.id ? identifiedUpdatedReminder : item
          )
        )
      );
      showToast(
        'success',
        t(
          isActive ? translations.reminders.active_success : translations.reminders.inactive_success
        )
      );
    } catch (error) {
      console.debug('Could not update senior reminder status.', error);
      showToast('error', t(translations.reminders.active_error));
    } finally {
      setPendingActiveIds((current) => current.filter((id) => id !== reminder.id));
    }
  };

  const handleFormClose = () => {
    if (!isSaving) setFormTarget(undefined);
  };

  const pageHeader = (
    <GenericHeader
      isLoading={isLoading}
      pageTitle={t(translations.reminders.title)}
      leftColumn={<Box />}
      rightColumn={
        canManageReminders ? (
          <Button
            variant="inverted"
            color="primary"
            startIcon={<Add />}
            disabled={!currentUserId}
            onClick={handleCreateReminder}
          >
            {t(translations.reminders.add)}
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <ContentPage fullWidth>
      <Box>
        {pageHeader}
        <Box sx={{mt: 3}}>
          {loadError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={refreshFirstPage}>
                  {t(translations.reminders.retry)}
                </Button>
              }
            >
              {t(translations.reminders.load_error)}
            </Alert>
          ) : isLoading ? (
            <LoadingContent />
          ) : reminders.length === 0 ? (
            <Stack spacing={1}>
              <Typography>{t(translations.reminders.empty_title)}</Typography>
              <Typography color="text.secondary">
                {t(translations.reminders.empty_description)}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1}>
              {reminders.map((reminder) => (
                <ReminderCard
                  key={reminder.id}
                  reminder={reminder}
                  activeUpdatePending={pendingActiveIds.includes(reminder.id)}
                  canManage={canManageReminders}
                  onEdit={handleEditReminder}
                  onDelete={handleOpenDelete}
                  onToggleActive={handleToggleActive}
                />
              ))}
              {loadMoreError && (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={handleLoadMore}>
                      {t(translations.reminders.retry)}
                    </Button>
                  }
                >
                  {t(translations.reminders.load_error)}
                </Alert>
              )}
              {nextServerSkip < totalCount && (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                  <Button
                    variant="outlined"
                    disabled={isLoadingMore}
                    startIcon={isLoadingMore ? <CircularProgress size={16} /> : <Refresh />}
                    onClick={handleLoadMore}
                  >
                    {isLoadingMore
                      ? t(translations.reminders.loading)
                      : t(translations.reminders.load_more)}
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>
      {canWrite && formTarget && (
        <ReminderFormDialog
          key={formTarget.reminder?.id || 'new-reminder'}
          open
          reminder={formTarget.reminder}
          isSubmitting={isSaving}
          onClose={handleFormClose}
          onSave={handleSaveReminder}
        />
      )}
      {canWrite && deleteTarget && (
        <DeleteResourceConfirmationModal
          open
          title={t(translations.reminders.delete_title)}
          description={t(translations.reminders.delete_description)}
          onCancel={() => {
            if (!isDeleting) {
              setDeleteTarget(undefined);
            }
          }}
          onConfirm={handleDeleteReminder}
          isDeleting={isDeleting}
          cancel_label={t(translations.reminders.cancel)}
          confirm_label={t(translations.reminders.delete)}
        />
      )}
    </ContentPage>
  );
}

export function RemindersUnavailable() {
  const {t} = useTranslation();
  return (
    <ContentPage fullWidth>
      <Box>
        <GenericHeader
          isLoading={false}
          pageTitle={t(translations.reminders.title)}
          leftColumn={<Box />}
        />
        <Box sx={{mt: 3}}>
          <Alert severity="info">{t(translations.reminders.senior_unavailable)}</Alert>
        </Box>
      </Box>
    </ContentPage>
  );
}

export interface RemindersApi {
  get(
    limit: number,
    skip: number,
    seniorId: string,
    cancelToken?: CancelToken
  ): Promise<{data: Reminder[]; totalCount: number}>;
  create(body: PostReminder): Promise<Reminder>;
  updateById(id: string, body: PutReminder): Promise<Reminder>;
  updateActiveStatusById(id: string, isActive: boolean): Promise<Reminder>;
  deleteById(id: string): Promise<void>;
}

const REMINDER_PAGE_SIZE = 10;

type IdentifiedReminder = Reminder & {id: string};

interface RemindersContentProps {
  seniorId: string;
  api: RemindersApi;
  canWrite?: boolean;
}

const getReminderTimestamp = (value: Date | string): number =>
  value instanceof Date ? value.getTime() : new Date(value).getTime();

const sortReminders = <T extends Reminder>(items: Array<T>): Array<T> =>
  [...items].sort((left, right) => {
    const leftAt = getReminderTimestamp(left.nextRunAt || left.startAt);
    const rightAt = getReminderTimestamp(right.nextRunAt || right.startAt);
    return leftAt - rightAt;
  });

const remindersForSenior = (items: Array<Reminder>, seniorId: string): Array<IdentifiedReminder> =>
  items.filter(
    (reminder): reminder is IdentifiedReminder =>
      reminder.toUserId === seniorId && typeof reminder.id === 'string' && reminder.id.length > 0
  );
